import React, { useMemo } from 'react';
import {
  Box,
  Card,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TerminalIcon from '@mui/icons-material/Terminal';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';

const sevColor = {
  critical: '#800080',
  high: '#f44336',
  medium: '#ff9800',
  low: '#2196f3',
  info: '#4caf50',
  informational: '#4caf50',
};

const methodColor = {
  GET: '#DCEEFF',
  POST: '#E3F4E8',
  PUT: '#FFF3D5',
  DELETE: '#FFE3DE',
  PATCH: '#F2E6FF',
};

const methodTextColor = {
  GET: '#0B5CAD',
  POST: '#1C7C3F',
  PUT: '#9A5B00',
  DELETE: '#C0392B',
  PATCH: '#7A2CB5',
};

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(String(text || ''));
  } catch {}
};

const parseLocation = (value) => {
  if (!value) return { path: '—', method: '' };
  const parts = String(value).split(' - ');
  if (parts.length === 2) return { path: parts[0], method: parts[1].toUpperCase() };
  const match = String(value).match(/(.*)\s+-\s+([A-Z]+)$/i);
  if (match) return { path: match[1], method: match[2].toUpperCase() };
  return { path: String(value), method: '' };
};

const ESC = String.fromCharCode(27);
const ANSI_PATTERN = new RegExp(`${ESC}\\[[0-9;]*[mGKHF]`, 'g');
const stripAnsi = (value = '') => String(value).replace(ANSI_PATTERN, '');

const parseLogMessages = (raw = '') => {
  const text = String(raw || '').replace(/\r\n/g, '\n').trim();
  if (!text) return [];

  const lines = text.split('\n');
  const items = [];
  let current = null;

  const push = () => {
    if (current && String(current.content || '').trim()) {
      items.push({ ...current, content: String(current.content).trim() });
    }
    current = null;
  };

  const start = (type, initial = '') => {
    push();
    current = { type, content: initial ? String(initial).trim() : '' };
  };

  for (const line of lines) {
    const match = line.match(/^(Suggestion|Commands|Commands Observation|Observation)\s*:\s*(.*)$/i);
    if (match) {
      const label = String(match[1]).toLowerCase();
      const rest = match[2] ?? '';
      if (label === 'suggestion') start('Suggestion', rest);
      else if (label === 'commands') start('Commands', rest);
      else start('Observation', rest);
    } else {
      if (!current) current = { type: 'Observation', content: '' };
      current.content += (current.content ? '\n' : '') + line;
    }
  }

  push();
  return items;
};

const extractCommands = (content = '') => {
  const source = String(content || '').trim();
  if (!source) return [];
  const out = [];
  const regex = /'([^']*)'|"([^"]*)"/g;
  let match;
  while ((match = regex.exec(source)) !== null) {
    const hit = (match[1] ?? match[2] ?? '').trim();
    if (hit) out.push(hit);
  }
  return out.length ? out : [source];
};

const getLogTheme = (type) => {
  const value = String(type || '').toLowerCase();
  if (value === 'suggestion') {
    return {
      label: 'Suggestion',
      border: '#1976d2',
      chipBg: 'rgba(25,118,210,0.18)',
      chipColor: '#1976d2',
      bg: 'rgba(25,118,210,0.06)',
      icon: <AutoAwesomeIcon fontSize="small" />,
    };
  }
  if (value === 'commands') {
    return {
      label: 'Commands',
      border: '#000000',
      chipBg: 'rgba(0,0,0,0.10)',
      chipColor: '#000000',
      bg: 'rgba(0,0,0,0.03)',
      icon: <TerminalIcon fontSize="small" />,
    };
  }
  return {
    label: 'Observation',
    border: '#2e7d32',
    chipBg: 'rgba(46,125,50,0.14)',
    chipColor: '#2e7d32',
    bg: 'rgba(46,125,50,0.06)',
    icon: <VisibilityIcon fontSize="small" />,
  };
};

const ExploitsModal = ({ open, handleClose, exploit, mode = 'report' }) => {
  const severity = String(exploit?.severity || '').toLowerCase();
  const severityBackground = sevColor[severity] || '#9e9e9e';
  const title = exploit?.name || 'Exploit';
  const { path, method } = useMemo(() => parseLocation(exploit?.impacted_assets), [exploit]);
  const logItems = useMemo(() => parseLogMessages(stripAnsi(exploit?.command_output || '')), [exploit?.command_output]);

  const drawerWidth =
    mode === 'report'
      ? { xs: '94vw', sm: 720, md: 920 }
      : { xs: '94vw', sm: 560, md: 640 };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: drawerWidth,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Chip
            size="small"
            label={(exploit?.severity || 'SEV').toUpperCase()}
            sx={{ bgcolor: severityBackground, color: '#fff', fontWeight: 800 }}
          />
          <Typography variant="h6" sx={{ fontWeight: 900 }} noWrap title={title}>
            {mode === 'report' ? 'Report' : 'Agent Logs'} - {title}
          </Typography>
        </Stack>

        <Tooltip title="Close" arrow>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {!exploit ? (
        <Typography>No exploit selected.</Typography>
      ) : mode === 'report' ? (
        <>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
            {!!method && (
              <Chip
                size="small"
                label={method}
                sx={{
                  bgcolor: methodColor[method] || '#e0e0e0',
                  color: methodTextColor[method] || '#000',
                  fontWeight: 800,
                }}
              />
            )}
            <Chip size="small" variant="outlined" label={path} sx={{ maxWidth: 420 }} />
            <Tooltip title="Copy report markdown" arrow>
              <IconButton size="small" onClick={() => copy(exploit?.report_details || '')}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color="text.secondary">
              Markdown report from `report_details`
            </Typography>
          </Stack>

          <Box sx={{ overflowY: 'auto', pr: 0.5 }}>
            <ReactMarkdown
              components={{
                code({ className, children, ...props }) {
                  const isBlock = className && className.startsWith('language-');
                  if (!isBlock) return <code {...props}>{children}</code>;
                  const language = className?.replace('language-', '') || 'text';
                  return (
                    <SyntaxHighlighter
                      language={language}
                      style={prism}
                      wrapLines
                      showLineNumbers
                      customStyle={{ borderRadius: 10, margin: 0, fontSize: '0.9rem' }}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  );
                },
              }}
            >
              {exploit?.report_details || 'No report details available.'}
            </ReactMarkdown>
          </Box>
        </>
      ) : (
        <>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
            {!!method && (
              <Chip
                size="small"
                label={method}
                sx={{
                  bgcolor: methodColor[method] || '#e0e0e0',
                  color: methodTextColor[method] || '#000',
                  fontWeight: 800,
                }}
              />
            )}
            <Chip size="small" variant="outlined" label={path} sx={{ maxWidth: 420 }} />
            <Tooltip title="Copy full command_output" arrow>
              <IconButton size="small" onClick={() => copy(exploit?.command_output || '')}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color="text.secondary">
              Parsed from `command_output` into Suggestion / Commands / Observation
            </Typography>
          </Stack>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              pr: 0.5,
              pb: 1,
            }}
          >
            <Stack spacing={1.5}>
              {logItems.length ? (
                logItems.map((item, index) => {
                  const theme = getLogTheme(item.type);
                  const value = String(item.type || '').toLowerCase();

                  if (value === 'commands') {
                    const commands = extractCommands(item.content);

                    return (
                      <Card
                        key={`${theme.label}-${index}`}
                        variant="outlined"
                        sx={{
                          borderRadius: 2.5,
                          p: 1.5,
                          borderLeft: `6px solid ${theme.border}`,
                          bgcolor: theme.bg,
                          overflow: 'hidden',
                        }}
                      >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box sx={{ color: theme.chipColor, display: 'flex', alignItems: 'center' }}>{theme.icon}</Box>
                            <Chip
                              size="small"
                              label={theme.label}
                              sx={{
                                bgcolor: theme.chipBg,
                                color: theme.chipColor,
                                fontWeight: 900,
                                letterSpacing: 0.3,
                              }}
                            />
                          </Stack>

                          <Tooltip title="Copy commands" arrow>
                            <IconButton size="small" onClick={() => copy(commands.join('\n'))}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>

                        <Stack spacing={1}>
                          {commands.map((command, commandIndex) => (
                            <Box key={`${index}-cmd-${commandIndex}`}>
                              <SyntaxHighlighter
                                language="bash"
                                style={prism}
                                wrapLines
                                customStyle={{
                                  borderRadius: 10,
                                  margin: 0,
                                  fontSize: '0.88rem',
                                }}
                              >
                                {String(command || '').trim()}
                              </SyntaxHighlighter>
                            </Box>
                          ))}
                        </Stack>
                      </Card>
                    );
                  }

                  return (
                    <Card
                      key={`${theme.label}-${index}`}
                      variant="outlined"
                      sx={{
                        borderRadius: 2.5,
                        p: 1.5,
                        borderLeft: `6px solid ${theme.border}`,
                        bgcolor: theme.bg,
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{ color: theme.chipColor, display: 'flex', alignItems: 'center' }}>{theme.icon}</Box>
                          <Chip
                            size="small"
                            label={theme.label}
                            sx={{
                              bgcolor: theme.chipBg,
                              color: theme.chipColor,
                              fontWeight: 900,
                              letterSpacing: 0.3,
                            }}
                          />
                        </Stack>

                        <Tooltip title={`Copy ${theme.label.toLowerCase()}`} arrow>
                          <IconButton size="small" onClick={() => copy(item.content)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>

                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
                        {item.content || 'No details available.'}
                      </Typography>
                    </Card>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No agent log details available.
                </Typography>
              )}
            </Stack>
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default ExploitsModal;
